/**
 * Shared UI primitives. Every screen imports from here so the brand stays
 * consistent. Tailwind classes; component classes (.btn, .card, .pill) live in
 * index.css.
 */
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { CircleNotch, Tray, X, type Icon } from '@phosphor-icons/react';
import type { AccessStatus, ModuleProgressState, ModuleStatus, SubmissionStatus } from '@cvs/shared';
import { initials } from '../lib/format';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ── Button ───────────────────────────────────────────────────────────────────
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  loading?: boolean;
  icon?: Icon;
}
const VARIANT_CLASS: Record<Variant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  ghost: 'btn-ghost',
  danger: 'btn-danger',
};
export function Button({ variant = 'primary', loading, icon: IconCmp, children, className, disabled, ...rest }: ButtonProps) {
  return (
    <button className={cx(VARIANT_CLASS[variant], className)} disabled={disabled || loading} {...rest}>
      {loading ? (
        <CircleNotch size={18} className="animate-spin" />
      ) : IconCmp ? (
        <IconCmp size={18} weight="bold" />
      ) : null}
      {children}
    </button>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────────
export function Card({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx('card', className)} {...rest}>
      {children}
    </div>
  );
}

// ── Pill + status pills ──────────────────────────────────────────────────────
export type PillTone = 'teal' | 'green' | 'amber' | 'rose' | 'slate' | 'blue' | 'violet';
const TONE_CLASS: Record<PillTone, string> = {
  teal: 'bg-teal-50 text-teal-700',
  green: 'bg-emerald-50 text-emerald-700',
  amber: 'bg-amber-50 text-amber-700',
  rose: 'bg-rose-50 text-rose-700',
  slate: 'bg-slate-100 text-slate-600',
  blue: 'bg-sky-50 text-sky-700',
  violet: 'bg-violet-50 text-violet-700',
};
export function Pill({ tone = 'slate', children, className }: { tone?: PillTone; children: ReactNode; className?: string }) {
  return <span className={cx('pill', TONE_CLASS[tone], className)}>{children}</span>;
}

export function AccessPill({ status }: { status: AccessStatus }) {
  const map: Record<AccessStatus, [PillTone, string]> = {
    active: ['green', 'Active'],
    pending: ['amber', 'Pending'],
    revoked: ['rose', 'Revoked'],
  };
  const [tone, label] = map[status];
  return <Pill tone={tone}>{label}</Pill>;
}

export function ModuleStatePill({ state }: { state: ModuleProgressState }) {
  const map: Record<ModuleProgressState, [PillTone, string]> = {
    completed: ['green', 'Completed'],
    'in-progress': ['teal', 'In progress'],
    available: ['blue', 'Available'],
    locked: ['slate', 'Locked'],
  };
  const [tone, label] = map[state];
  return <Pill tone={tone}>{label}</Pill>;
}

export function ContentStatusPill({ status }: { status: ModuleStatus }) {
  const map: Record<ModuleStatus, [PillTone, string]> = {
    published: ['green', 'Published'],
    recording: ['amber', 'Recording'],
    planned: ['slate', 'Planned'],
  };
  const [tone, label] = map[status];
  return <Pill tone={tone}>{label}</Pill>;
}

export function SubmissionPill({ status }: { status: SubmissionStatus }) {
  const map: Record<SubmissionStatus, [PillTone, string]> = {
    submitted: ['amber', 'Awaiting review'],
    graded: ['green', 'Graded'],
  };
  const [tone, label] = map[status];
  return <Pill tone={tone}>{label}</Pill>;
}

// ── Progress indicators ──────────────────────────────────────────────────────
export function ProgressBar({ value, max = 100, className }: { value: number; max?: number; className?: string }) {
  const p = max === 0 ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className={cx('h-2 w-full overflow-hidden rounded-full bg-slate-200', className)}>
      <div className="h-full rounded-full bg-brand-teal transition-all" style={{ width: `${p}%` }} />
    </div>
  );
}

export function ProgressRing({
  value,
  size = 128,
  stroke = 12,
  children,
}: {
  value: number;
  size?: number;
  stroke?: number;
  children?: ReactNode;
}) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, value));
  const offset = circ - (p / 100) * circ;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} className="text-slate-200" stroke="currentColor" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          className="text-brand-teal"
          stroke="currentColor"
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">{children}</div>
    </div>
  );
}

// ── Loaders / states ─────────────────────────────────────────────────────────
export function Spinner({ size = 20, className }: { size?: number; className?: string }) {
  return <CircleNotch size={size} className={cx('animate-spin text-brand-teal', className)} />;
}
export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-slate-500">
      <Spinner /> <span>{label}</span>
    </div>
  );
}
export function FullScreenLoader() {
  return (
    <div className="flex h-screen items-center justify-center bg-brand-light">
      <Spinner size={32} />
    </div>
  );
}
export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <Card className="p-8 text-center">
      <p className="font-semibold text-rose-600">{message}</p>
      {onRetry && (
        <Button variant="secondary" className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      )}
    </Card>
  );
}
export function EmptyState({
  icon: IconCmp = Tray,
  title,
  description,
  action,
}: {
  icon?: Icon;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <Card className="flex flex-col items-center justify-center gap-3 p-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
        <IconCmp size={24} />
      </div>
      <div>
        <p className="font-semibold text-brand-navy">{title}</p>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      {action}
    </Card>
  );
}

// ── Tabs ─────────────────────────────────────────────────────────────────────
export interface TabItem {
  id: string;
  label: ReactNode;
  icon?: Icon;
}
export function Tabs({
  items,
  active,
  onChange,
  className,
}: {
  items: TabItem[];
  active: string;
  onChange: (id: string) => void;
  className?: string;
}) {
  return (
    <div className={cx('flex gap-1 overflow-x-auto border-b border-slate-200', className)} role="tablist">
      {items.map((t) => {
        const selected = t.id === active;
        const I = t.icon;
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(t.id)}
            className={cx(
              'flex shrink-0 items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition',
              selected ? 'border-brand-teal text-brand-teal' : 'border-transparent text-slate-500 hover:text-slate-800',
            )}
          >
            {I && <I size={18} />}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────────
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-brand-navy/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <Card className="relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden p-0">
        <div className="flex shrink-0 items-center justify-between px-6 pb-4 pt-6">
          <h3 className="text-lg font-bold">{title}</h3>
          <button onClick={onClose} className="btn-ghost -mr-2 p-2" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">{children}</div>
        {footer && (
          <div className="flex shrink-0 justify-end gap-3 border-t border-slate-100 px-6 py-4">{footer}</div>
        )}
      </Card>
    </div>
  );
}

// ── Headings / misc ──────────────────────────────────────────────────────────
export function SectionHeading({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
      <div>
        {eyebrow && <div className="label-mono mb-1">{eyebrow}</div>}
        <h2 className="text-xl font-bold sm:text-2xl">{title}</h2>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function Avatar({ name, className }: { name: string; className?: string }) {
  return (
    <div
      className={cx(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-teal/15 text-sm font-bold text-brand-teal',
        className,
      )}
    >
      {initials(name)}
    </div>
  );
}

export function StatCard({
  icon: IconCmp,
  label,
  value,
  hint,
  tone = 'teal',
}: {
  icon?: Icon;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: PillTone;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="label-mono">{label}</span>
        {IconCmp && (
          <span className={cx('flex h-8 w-8 items-center justify-center rounded-lg', TONE_CLASS[tone])}>
            <IconCmp size={18} weight="bold" />
          </span>
        )}
      </div>
      <div className="mt-3 text-2xl font-bold text-brand-navy">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </Card>
  );
}
